import generateDatapointSearchColumns from '@components/Home/Historian/AddByTopic/DataPointSearchGridColumns';
import DatapointSearchToolbar from '@components/Home/Historian/AddByTopic/DataPointSearchToolbar';
import UseArchivistPagination from '@hooks/api/queries/archivist/useArchivistGetPagination';
import {
  Clear as ClearIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import QueryKeys from '@src/constants/queryKeys';
import {
  ArchivePoint,
  ArchivistPaginationResponse,
  filterObject,
} from '@src/types/Archivist';
import { PaginationModel } from '@src/types/pagination/PaginationModel';
import { HistorianDataPoint } from '@types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface DataPointSearchModalProps {
  show: boolean;
  onHide: () => void;
  onAddDataPoints: (selectedDataPoints: HistorianDataPoint[]) => void;
}

const ensureDefaultFilters = (filters: filterObject[] = []): filterObject[] => {
  const defaultFilter = { field: 'isParsed', value: 'true' };
  const otherFilters = filters.filter((f) => f.field !== 'isParsed');
  return [defaultFilter, ...otherFilters];
};

const DataPointSearchModal: React.FC<DataPointSearchModalProps> = ({
  show,
  onHide,
  onAddDataPoints,
}) => {
  const [_requestBodyDetails, setRequestBodyDetails] = useState<Record<
    string,
    string
  > | null>(null);

  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: 25,
  });

  const [filterParams, setFilterParams] = useState<{
    filters?: filterObject[];
    search?: string;
  }>({
    filters: [{ field: 'isParsed', value: 'true' }],
  });

  const [searchText, setSearchText] = useState('');

  const [selectedDataPointsCache, setSelectedDataPointsCache] = useState<
    Map<string, ArchivePoint>
  >(new Map());

  const filterHashRef = useRef<string>('');

  const isUpdatingSelectionRef = useRef(false);

  const queryParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      page_size: paginationModel.pageSize,
      ...filterParams,
    }),
    [paginationModel.page, paginationModel.pageSize, filterParams],
  );

  const queryOptions = useMemo(
    () => ({
      enabled: show,
      queryKeys: [
        QueryKeys['get-pagination'],
        'datapoint-search',
        paginationModel.page.toString(),
        paginationModel.pageSize.toString(),
        JSON.stringify(filterParams.filters || []),
        filterParams.search || '',
      ],
    }),
    [show, paginationModel.page, paginationModel.pageSize, filterParams],
  );

  const { data, isLoading } = UseArchivistPagination({
    params: queryParams,
    options: queryOptions,
  }) as {
    data: ArchivistPaginationResponse | undefined;
    isLoading: boolean;
    refetch: () => void;
  };

  const [archiveRows, setRows] = useState<ArchivePoint[]>([]);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);

  // Clear cache when filters or search changes
  useEffect(() => {
    const currentFilterHash = JSON.stringify({
      filters: filterParams.filters,
      search: filterParams.search,
    });

    if (filterHashRef.current && filterHashRef.current !== currentFilterHash) {
      setSelectedDataPointsCache(new Map());
    }

    filterHashRef.current = currentFilterHash;
  }, [filterParams]);

  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      setRows(data.data);
      setTotalRowCount(data.total_count || 0);
    } else if (data && !data.data) {
      setRows([]);
      setTotalRowCount(0);
    }
  }, [data]);

  const currentPageSelectionModel = useMemo(() => {
    if (!archiveRows.length) {
      return [];
    }

    return archiveRows.reduce<string[]>((acc, row) => {
      if (selectedDataPointsCache.has(row._id.$oid)) {
        acc.push(row._id.$oid);
      }
      return acc;
    }, []);
  }, [archiveRows, selectedDataPointsCache]);

  const handleDetailsClick = useCallback(
    (gridRow: GridValidRowModel) => (): void => {
      setRequestBodyDetails(gridRow.body);
    },
    [],
  );

  const archiveColumns: GridColDef[] = useMemo(
    () =>
      generateDatapointSearchColumns(handleDetailsClick).map((col) => ({
        ...col,
      })),
    [handleDetailsClick],
  );

  const handleDropdownFiltersChange = useCallback((filters: filterObject[]) => {
    setFilterParams((prev) => ({
      ...prev,
      filters: ensureDefaultFilters(filters),
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFilterParams((prev) => ({
      ...prev,
      search: searchText || undefined,
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const clearSearch = () => {
    setSearchText('');
    setFilterParams((prev) => ({
      filters: ensureDefaultFilters(
        prev.filters?.filter((f) => f.field !== 'search'),
      ),
      search: undefined,
    }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (
    paginationEvent: GridPaginationModel,
  ): void => {
    const newPaginationModel = {
      page: paginationEvent.page,
      pageSize: paginationEvent.pageSize,
    };
    setPaginationModel(newPaginationModel);
  };

  const handleSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      if (isUpdatingSelectionRef.current) {
        return;
      }

      const newCache = new Map(selectedDataPointsCache);

      const currentPageRowsById = new Map(
        archiveRows.map((row) => [row._id.$oid, row]),
      );

      const newSelectionSet = new Set(newSelectionModel);
      const currentCacheIds = new Set(
        Array.from(currentPageRowsById.keys()).filter((id) =>
          selectedDataPointsCache.has(id),
        ),
      );

      newSelectionModel.forEach((selectedId) => {
        const row = currentPageRowsById.get(selectedId.toString());
        if (row) {
          newCache.set(selectedId.toString(), row);
        }
      });

      currentCacheIds.forEach((cachedId) => {
        if (!newSelectionSet.has(cachedId)) {
          newCache.delete(cachedId);
        }
      });

      setSelectedDataPointsCache(newCache);
    },
    [archiveRows, selectedDataPointsCache],
  );

  const convertArchivePointToHistorianDataPoint = (
    archivePoint: ArchivePoint,
  ): HistorianDataPoint => {
    let unitId = archivePoint.deviceType?.includes('unitController')
      ? archivePoint.deviceId
      : archivePoint.parentDeviceId;
    let sourceDeviceId = archivePoint.deviceId || '';
    let parentSourceDeviceId = '';

    // Special handling for some device types, simplified logic could be made in the future when more devices are added
    if (sourceDeviceId && sourceDeviceId.includes('_mp_')) {
      const parts = sourceDeviceId.split('_mp_');
      if (parts.length === 2) {
        parentSourceDeviceId = parts[0];
      }
    } else if (sourceDeviceId && sourceDeviceId.includes('_rack_')) {
      const parts = sourceDeviceId.split('_rack_');
      if (parts.length === 2) {
        parentSourceDeviceId = parts[0];
      }
    } else if (sourceDeviceId && sourceDeviceId.includes('_powinbess_')) {
      const parts = sourceDeviceId.split('_powinbess_');
      if (parts.length === 2) {
        unitId = 'powinbess_' + parts[1];
        parentSourceDeviceId = parts[0];
      }
    }

    return {
      axis: 'Y1',
      category: archivePoint.deviceType || '',
      siteId: archivePoint.siteId || '',
      unitId: unitId || '',
      sourceDeviceId,
      dataPoint: {
        pointName: archivePoint.pointName || '',
        dataType: archivePoint.dataType || '',
        displayName: null,
      },
      topic: archivePoint.topic,
      parentSourceDeviceId,
    };
  };

  const handleAddDataPoints = () => {
    const selectedDataPoints = Array.from(selectedDataPointsCache.values());
    if (selectedDataPoints.length > 0) {
      const convertedDataPoints = selectedDataPoints.map(
        convertArchivePointToHistorianDataPoint,
      );
      onAddDataPoints(convertedDataPoints);
      onHide();
    }
  };

  const handleClose = () => {
    setSelectedDataPointsCache(new Map());
    onHide();
  };

  const getRowId = useMemo(
    () => (row: ArchivePoint) => {
      return row?._id?.$oid;
    },
    [],
  );

  const selectedCount = selectedDataPointsCache.size;

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth='xl'
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        Datapoint Search
        <IconButton onClick={handleClose} sx={{ color: 'text.primary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <DatapointSearchToolbar
          onFiltersChange={handleDropdownFiltersChange}
          currentFilters={filterParams.filters || []}
        />

        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            component='form'
            onSubmit={handleSearchSubmit}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <TextField
              fullWidth
              size='small'
              placeholder='Search across all fields...'
              value={searchText}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type='submit'
              variant='outlined'
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Search
            </Button>
            {(searchText || filterParams.search) && (
              <Button
                onClick={clearSearch}
                variant='outlined'
                startIcon={<ClearIcon />}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Clear
              </Button>
            )}
          </Box>
          {filterParams.search && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mt: 1, display: 'block' }}
            >
              Searching for: "{filterParams.search}"
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGridPro
            density='compact'
            pageSizeOptions={[10, 25, 50, 100]}
            pagination
            rowCount={totalRowCount}
            paginationMode='server'
            columns={archiveColumns}
            rows={archiveRows}
            loading={isLoading}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            getRowId={getRowId}
            checkboxSelection
            rowSelectionModel={currentPageSelectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            sx={{ height: '100%' }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'space-between',
          borderTop: 1,
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box>
          {selectedCount > 0 && (
            <Typography variant='body2' color='text.secondary'>
              {selectedCount} datapoint{selectedCount !== 1 ? 's' : ''} selected
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleAddDataPoints}
            disabled={selectedCount === 0}
            variant='contained'
          >
            Add Selected Datapoints
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DataPointSearchModal;
